import type { CheckoutDiscountComponent } from "@lemmonhq/types";

export type DiscountableItem = {
  key: string;
  label: string;
  detail: string;
  total: number;
  quantity: number;
  orderItemId?: string;
  cartItemId?: string;
};

/**
 * Computes per-item existing discount amounts from order-items discount components.
 * For percentage: applies the percentage to the item total.
 * For fixed-amount: prorates by item total across covered items.
 */
export const computePerItemDiscounts = (
  items: DiscountableItem[],
  components: CheckoutDiscountComponent[],
): Record<string, number> => {
  const result: Record<string, number> = {};

  for (const component of components) {
    if (component.source !== "order-items") continue;

    const coveredItems = items.filter(
      (item) =>
        (item.orderItemId &&
          component.metadata?.order_item_ids?.includes(item.orderItemId)) ||
        (item.cartItemId &&
          component.metadata?.cart_items_ids?.includes(item.cartItemId)),
    );

    if (coveredItems.length === 0) continue;

    if (component.value_type === "percentage") {
      for (const item of coveredItems) {
        result[item.key] =
          (result[item.key] ?? 0) +
          (item.total * component.input_value) / 100;
      }
    } else {
      // Fixed amount: prorate proportionally by item total
      const coveredTotal = coveredItems.reduce((s, i) => s + i.total, 0);
      for (const item of coveredItems) {
        const share =
          coveredTotal > 0
            ? item.total / coveredTotal
            : 1 / coveredItems.length;
        result[item.key] =
          (result[item.key] ?? 0) + component.discount_amount * share;
      }
    }
  }

  return result;
};

export const computeSelectedTotal = (
  items: DiscountableItem[],
  selectedQuantities: Record<string, number>,
): number =>
  items.reduce((sum, item) => {
    const qty = selectedQuantities[item.key] ?? 0;
    if (qty <= 0) return sum;
    return sum + (item.total / item.quantity) * qty;
  }, 0);

export const computeExistingDiscountOnSelected = (
  items: DiscountableItem[],
  selectedQuantities: Record<string, number>,
  perItemDiscounts: Record<string, number>,
): number =>
  items.reduce((sum, item) => {
    const qty = selectedQuantities[item.key] ?? 0;
    if (qty <= 0) return sum;
    const itemDiscount = perItemDiscounts[item.key] ?? 0;
    // Prorate by selected quantity
    return sum + itemDiscount * (qty / item.quantity);
  }, 0);
